/**
 * Performance calculation functions for broiler production metrics
 */

/**
 * Calculate Feed Conversion Ratio (FCR)
 * FCR = Total Feed Consumed / Total Live Weight Produced
 * 
 * @param feedConsumed - Total feed consumed in kg
 * @param totalWeight - Total live weight produced in kg
 * @returns FCR value
 */
export function calculateFCR(feedConsumed: number, totalWeight: number): number {
  if (totalWeight <= 0) {
    throw new Error('Total weight must be greater than 0');
  }
  return feedConsumed / totalWeight;
}

/**
 * Calculate survival rate percentage
 * Survival Rate = (Total Harvested Count / Initial Placement Count) × 100
 * 
 * @param totalCount - Total number of birds harvested
 * @param initialCount - Initial number of birds placed
 * @returns Survival rate as percentage
 */
export function calculateSurvivalRate(totalCount: number, initialCount: number): number {
  if (initialCount <= 0) {
    throw new Error('Initial count must be greater than 0');
  }
  return (totalCount / initialCount) * 100;
}

/**
 * Calculate European Production Efficiency Factor (EPEF)
 * EPEF = (Average Weight × Survival Rate × 100) / (Cycle Days × FCR)
 * 
 * @param avgWeight - Average weight per bird in kg
 * @param survivalRate - Survival rate as percentage
 * @param days - Production cycle duration in days
 * @param fcr - Feed Conversion Ratio
 * @returns EPEF value
 */
export function calculateEPEF(
  avgWeight: number,
  survivalRate: number,
  days: number,
  fcr: number
): number {
  if (days <= 0) {
    throw new Error('Cycle days must be greater than 0');
  }
  if (fcr <= 0) {
    throw new Error('FCR must be greater than 0');
  }
  return (avgWeight * survivalRate * 100) / (days * fcr);
}

/**
 * Find target FCR from the FCR scale table based on average weight
 * Selects the table entry with minimum absolute difference in weight
 * 
 * @param avgWeight - Average live weight in kg
 * @param fcrTable - FCR scale table as array of [weight, targetFCR] tuples
 * @returns Target FCR value from the table
 */
export function findTargetFCR(
  avgWeight: number,
  fcrTable: [number, number][]
): number {
  if (fcrTable.length === 0) {
    throw new Error('FCR table cannot be empty');
  }

  let closestIndex = 0;
  let minDifference = Math.abs(fcrTable[0][0] - avgWeight);

  for (let i = 1; i < fcrTable.length; i++) {
    const difference = Math.abs(fcrTable[i][0] - avgWeight);
    if (difference < minDifference) {
      minDifference = difference;
      closestIndex = i;
    }
  }

  return fcrTable[closestIndex][1];
}
